/**
 * FeaturesTab - Feature sections management
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Plus, Trash2, GripVertical, Upload } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function FeaturesTab({ content, setContent }) {
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const updateFeature = (index, field, value) => {
    setContent(prev => {
      const newFeatures = [...(prev.features || [])];
      newFeatures[index] = { ...newFeatures[index], [field]: value };
      return { ...prev, features: newFeatures };
    });
  };

  const addFeature = () => {
    setContent(prev => ({
      ...prev,
      features: [...(prev.features || []), {
        id: Date.now().toString(), badge: 'NEW FEATURE', title: 'Feature Title',
        description: 'Feature description goes here.', quote: '', image: ''
      }]
    }));
  };

  const removeFeature = (index) => {
    setContent(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
  };

  const handleImageUpload = async (index, file) => {
    if (!file) return;
    setUploadingIndex(index);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/landing/upload-feature-image`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      updateFeature(index, 'image', res.data.image_url);
    } catch (err) {
      console.error('Feature image upload failed:', err);
    } finally {
      setUploadingIndex(null);
    }
  };

  const resolveImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Features</CardTitle>
          <CardDescription>Manage feature sections</CardDescription>
        </div>
        <Button onClick={addFeature} variant="outline"><Plus className="w-4 h-4 mr-2" />Add Feature</Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Section Header */}
        <div className="bg-emerald-50 p-4 rounded-lg space-y-3">
          <Label className="font-semibold text-gray-700">Section Header</Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Badge Text</Label>
              <Input
                value={content.features_header?.badge || ''}
                onChange={(e) => setContent(prev => ({ ...prev, features_header: { ...prev.features_header, badge: e.target.value } }))}
                placeholder="e.g., FEATURES"
                data-testid="features-header-badge"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Title</Label>
              <Input
                value={content.features_header?.title || ''}
                onChange={(e) => setContent(prev => ({ ...prev, features_header: { ...prev.features_header, title: e.target.value } }))}
                placeholder="e.g., Everything You Need"
                data-testid="features-header-title"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Subtitle</Label>
              <Input
                value={content.features_header?.subtitle || ''}
                onChange={(e) => setContent(prev => ({ ...prev, features_header: { ...prev.features_header, subtitle: e.target.value } }))}
                placeholder="e.g., Tools to manage your team"
                data-testid="features-header-subtitle"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Background Colour (Light)</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={content.features_header?.bgColor || '#f9fafb'}
                  onChange={(e) => setContent(prev => ({ ...prev, features_header: { ...prev.features_header, bgColor: e.target.value } }))}
                  className="w-8 h-8 rounded border cursor-pointer"
                  data-testid="features-bg-color"
                />
                <Input
                  value={content.features_header?.bgColor || '#f9fafb'}
                  onChange={(e) => setContent(prev => ({ ...prev, features_header: { ...prev.features_header, bgColor: e.target.value } }))}
                  className="w-28 h-8 text-xs font-mono"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Background Colour (Dark)</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={content.features_header?.bgColorDark || '#1f2937'}
                  onChange={(e) => setContent(prev => ({ ...prev, features_header: { ...prev.features_header, bgColorDark: e.target.value } }))}
                  className="w-8 h-8 rounded border cursor-pointer"
                  data-testid="features-bg-color-dark"
                />
                <Input
                  value={content.features_header?.bgColorDark || '#1f2937'}
                  onChange={(e) => setContent(prev => ({ ...prev, features_header: { ...prev.features_header, bgColorDark: e.target.value } }))}
                  className="w-28 h-8 text-xs font-mono"
                />
              </div>
            </div>
          </div>
          <span className="text-[10px] text-gray-400">Also sets grass border colour to match</span>

          <div className="flex items-center justify-between pt-3 border-t border-gray-200">
            <div>
              <Label className="text-sm font-medium">Entry animation for feature images</Label>
              <p className="text-xs text-gray-500 mt-0.5">Each image animates in as the user scrolls. Toggle off to render them instantly with no motion.</p>
            </div>
            <Switch
              checked={!(content.features_header?.animation_disabled === true)}
              onCheckedChange={(checked) => setContent(prev => ({
                ...prev,
                features_header: { ...(prev.features_header || {}), animation_disabled: !checked }
              }))}
              data-testid="features-animation-toggle"
            />
          </div>

          {!(content.features_header?.animation_disabled === true) && (
            <div className="pt-1" data-testid="features-animation-mode-section">
              <Label className="text-sm font-medium">Animation style</Label>
              <p className="text-xs text-gray-500 mt-0.5 mb-2">Choose how each feature image enters the viewport.</p>
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: 'zoom', label: 'Zoom in', hint: 'Scale from 70% — calm, modern (recommended)' },
                  { value: 'slide', label: 'Slide in', hint: 'Glide in from the side (legacy)' },
                ].map(opt => {
                  const current = content.features_header?.phone_entry_animation === 'slide' ? 'slide' : 'zoom';
                  const selected = current === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setContent(prev => ({
                        ...prev,
                        features_header: { ...(prev.features_header || {}), phone_entry_animation: opt.value }
                      }))}
                      className={`flex flex-col items-start gap-0.5 px-3 py-2 rounded-lg border text-left transition-colors ${
                        selected
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700 bg-white'
                      }`}
                      data-testid={`features-animation-mode-${opt.value}`}
                    >
                      <span className="text-xs font-semibold">{opt.label}</span>
                      <span className="text-[10px] text-gray-500">{opt.hint}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-3 border-t border-gray-200">
            <div>
              <Label className="text-sm font-medium">Display screenshots in phone mockup frame</Label>
              <p className="text-xs text-gray-500 mt-0.5">When ON, each feature's uploaded image renders inside the same phone mockup used in the Gallery section. Best for app screenshots. Turn OFF to use the default Safari-style browser window frame.</p>
            </div>
            <Switch
              checked={content.features_header?.phone_frame === true}
              onCheckedChange={(checked) => setContent(prev => ({
                ...prev,
                features_header: { ...(prev.features_header || {}), phone_frame: checked }
              }))}
              data-testid="features-phone-frame-toggle"
            />
          </div>

          {content.features_header?.phone_frame === true && (
            <div className="pt-3 border-t border-gray-200 space-y-4" data-testid="features-phone-height-section">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Floor shadow under phone</Label>
                  <p className="text-xs text-gray-500 mt-0.5">Soft elliptical ground shadow that makes the phone feel like it's sitting on a surface. Renders below each feature's phone mockup.</p>
                </div>
                <Switch
                  checked={content.features_header?.phone_floor_shadow === true}
                  onCheckedChange={(checked) => setContent(prev => ({
                    ...prev,
                    features_header: { ...(prev.features_header || {}), phone_floor_shadow: checked }
                  }))}
                  data-testid="features-phone-floor-shadow-toggle"
                />
              </div>

              {content.features_header?.phone_floor_shadow === true && (
                <div className="pl-4 border-l-2 border-emerald-100" data-testid="features-phone-floor-shadow-offset-section">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <Label className="text-sm font-medium">Shadow vertical position</Label>
                      <p className="text-xs text-gray-500 mt-0.5">Nudge the shadow up (under phone) or down (more visible gap). 0 = default.</p>
                    </div>
                    <span className="text-xs font-mono text-gray-600" data-testid="features-phone-floor-shadow-offset-value">
                      {content.features_header?.phone_floor_shadow_offset || 0}px
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-gray-400 font-mono w-10 text-right">-30</span>
                    <input
                      type="range"
                      min="-30"
                      max="30"
                      step="1"
                      value={content.features_header?.phone_floor_shadow_offset || 0}
                      onChange={(e) => setContent(prev => ({
                        ...prev,
                        features_header: { ...(prev.features_header || {}), phone_floor_shadow_offset: Number(e.target.value) }
                      }))}
                      className="flex-1 accent-[#007a33]"
                      data-testid="features-phone-floor-shadow-offset-slider"
                    />
                    <span className="text-[10px] text-gray-400 font-mono w-10">+30</span>
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-sm font-medium">Phone mockup height</Label>
                    <p className="text-xs text-gray-500 mt-0.5">Overall height of the phone shell on the page.</p>
                  </div>
                  <span className="text-xs font-mono text-gray-600" data-testid="features-phone-height-value">
                    {content.features_header?.phone_height_px || 600}px
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-gray-400 font-mono w-10 text-right">360</span>
                  <input
                    type="range"
                    min="360"
                    max="900"
                    step="10"
                    value={content.features_header?.phone_height_px || 600}
                    onChange={(e) => setContent(prev => ({
                      ...prev,
                      features_header: { ...(prev.features_header || {}), phone_height_px: Number(e.target.value) }
                    }))}
                    className="flex-1 accent-[#007a33]"
                    data-testid="features-phone-height-slider"
                  />
                  <span className="text-[10px] text-gray-400 font-mono w-10">900</span>
                  {content.features_header?.phone_height_px && content.features_header.phone_height_px !== 600 && (
                    <button
                      type="button"
                      onClick={() => setContent(prev => ({
                        ...prev,
                        features_header: { ...(prev.features_header || {}), phone_height_px: 600 }
                      }))}
                      className="text-xs text-gray-500 underline hover:text-gray-800 whitespace-nowrap"
                      data-testid="features-phone-height-reset"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-sm font-medium">Phone mockup width</Label>
                    <p className="text-xs text-gray-500 mt-0.5">Overall width of the phone shell on the page. Tweak alongside height to get the look you want.</p>
                  </div>
                  <span className="text-xs font-mono text-gray-600" data-testid="features-phone-width-value">
                    {content.features_header?.phone_width_px || 300}px
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-gray-400 font-mono w-10 text-right">200</span>
                  <input
                    type="range"
                    min="200"
                    max="500"
                    step="10"
                    value={content.features_header?.phone_width_px || 300}
                    onChange={(e) => setContent(prev => ({
                      ...prev,
                      features_header: { ...(prev.features_header || {}), phone_width_px: Number(e.target.value) }
                    }))}
                    className="flex-1 accent-[#007a33]"
                    data-testid="features-phone-width-slider"
                  />
                  <span className="text-[10px] text-gray-400 font-mono w-10">500</span>
                  {content.features_header?.phone_width_px && content.features_header.phone_width_px !== 300 && (
                    <button
                      type="button"
                      onClick={() => setContent(prev => ({
                        ...prev,
                        features_header: { ...(prev.features_header || {}), phone_width_px: 300 }
                      }))}
                      className="text-xs text-gray-500 underline hover:text-gray-800 whitespace-nowrap"
                      data-testid="features-phone-width-reset"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>

              {/* Status bar controls */}
              <div className="pt-3 border-t border-gray-200 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Show status bar</Label>
                    <p className="text-xs text-gray-500 mt-0.5">The system tray at the top of the phone (time + signal / wifi / battery icons).</p>
                  </div>
                  <Switch
                    checked={content.features_header?.phone_show_status_bar !== false}
                    onCheckedChange={(checked) => setContent(prev => ({
                      ...prev,
                      features_header: { ...(prev.features_header || {}), phone_show_status_bar: checked }
                    }))}
                    data-testid="features-phone-statusbar-toggle"
                  />
                </div>

                {content.features_header?.phone_show_status_bar !== false && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-medium">Status bar background</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="color"
                            value={content.features_header?.phone_status_bar_bg || '#f3f4f6'}
                            onChange={(e) => setContent(prev => ({
                              ...prev,
                              features_header: { ...(prev.features_header || {}), phone_status_bar_bg: e.target.value }
                            }))}
                            className="h-9 w-12 rounded border"
                            data-testid="features-phone-statusbar-bg"
                          />
                          <input
                            type="text"
                            value={content.features_header?.phone_status_bar_bg || ''}
                            placeholder="auto"
                            onChange={(e) => setContent(prev => ({
                              ...prev,
                              features_header: { ...(prev.features_header || {}), phone_status_bar_bg: e.target.value }
                            }))}
                            className="flex-1 h-9 px-2 text-sm font-mono rounded border"
                          />
                          {content.features_header?.phone_status_bar_bg && (
                            <button type="button" onClick={() => setContent(prev => ({ ...prev, features_header: { ...(prev.features_header || {}), phone_status_bar_bg: '' } }))} className="text-xs text-gray-500 underline">Clear</button>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Status bar icon colour</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="color"
                            value={content.features_header?.phone_status_icon_color || '#111827'}
                            onChange={(e) => setContent(prev => ({
                              ...prev,
                              features_header: { ...(prev.features_header || {}), phone_status_icon_color: e.target.value }
                            }))}
                            className="h-9 w-12 rounded border"
                            data-testid="features-phone-statusbar-icon-color"
                          />
                          <input
                            type="text"
                            value={content.features_header?.phone_status_icon_color || ''}
                            placeholder="auto"
                            onChange={(e) => setContent(prev => ({
                              ...prev,
                              features_header: { ...(prev.features_header || {}), phone_status_icon_color: e.target.value }
                            }))}
                            className="flex-1 h-9 px-2 text-sm font-mono rounded border"
                          />
                          {content.features_header?.phone_status_icon_color && (
                            <button type="button" onClick={() => setContent(prev => ({ ...prev, features_header: { ...(prev.features_header || {}), phone_status_icon_color: '' } }))} className="text-xs text-gray-500 underline">Clear</button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-medium">Time display</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <select
                          value={content.features_header?.phone_time_mode || 'apple'}
                          onChange={(e) => setContent(prev => ({
                            ...prev,
                            features_header: { ...(prev.features_header || {}), phone_time_mode: e.target.value }
                          }))}
                          className="h-9 px-2 text-sm rounded border"
                          data-testid="features-phone-time-mode"
                        >
                          <option value="apple">Apple time (9:41)</option>
                          <option value="system">System time (live)</option>
                          <option value="custom">Custom time</option>
                        </select>
                        {content.features_header?.phone_time_mode === 'custom' && (
                          <input
                            type="text"
                            value={content.features_header?.phone_status_time || ''}
                            placeholder="e.g. 15:00"
                            onChange={(e) => setContent(prev => ({
                              ...prev,
                              features_header: { ...(prev.features_header || {}), phone_status_time: e.target.value }
                            }))}
                            className="flex-1 h-9 px-2 text-sm font-mono rounded border"
                            data-testid="features-phone-custom-time"
                          />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Browser bar controls */}
              <div className="pt-3 border-t border-gray-200 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Show browser bar</Label>
                    <p className="text-xs text-gray-500 mt-0.5">A Safari-style URL bar under the status bar. Best for web-app screenshots; turn OFF for native-app screenshots.</p>
                  </div>
                  <Switch
                    checked={content.features_header?.phone_show_browser_bar === true}
                    onCheckedChange={(checked) => setContent(prev => ({
                      ...prev,
                      features_header: { ...(prev.features_header || {}), phone_show_browser_bar: checked }
                    }))}
                    data-testid="features-phone-browserbar-toggle"
                  />
                </div>
                {content.features_header?.phone_show_browser_bar === true && (
                  <div>
                    <Label className="text-xs font-medium">Browser bar background</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={content.features_header?.phone_browser_bar_bg || '#f3f4f6'}
                        onChange={(e) => setContent(prev => ({
                          ...prev,
                          features_header: { ...(prev.features_header || {}), phone_browser_bar_bg: e.target.value }
                        }))}
                        className="h-9 w-12 rounded border"
                        data-testid="features-phone-browserbar-bg"
                      />
                      <input
                        type="text"
                        value={content.features_header?.phone_browser_bar_bg || ''}
                        placeholder="auto"
                        onChange={(e) => setContent(prev => ({
                          ...prev,
                          features_header: { ...(prev.features_header || {}), phone_browser_bar_bg: e.target.value }
                        }))}
                        className="flex-1 h-9 px-2 text-sm font-mono rounded border"
                      />
                      {content.features_header?.phone_browser_bar_bg && (
                        <button type="button" onClick={() => setContent(prev => ({ ...prev, features_header: { ...(prev.features_header || {}), phone_browser_bar_bg: '' } }))} className="text-xs text-gray-500 underline">Clear</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {content.features?.map((feature, index) => (
          <div key={feature.id || index} className="bg-gray-50 p-6 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GripVertical className="w-5 h-5 text-gray-400" />
                <span className="font-medium text-gray-700">Feature {index + 1}</span>
              </div>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => removeFeature(index)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Badge</Label><Input value={feature.badge} onChange={(e) => updateFeature(index, 'badge', e.target.value)} placeholder="e.g., SQUAD MANAGEMENT" data-testid={`feature-badge-${index}`} /></div>
              <div><Label>Title</Label><Input value={feature.title} onChange={(e) => updateFeature(index, 'title', e.target.value)} placeholder="Feature title" data-testid={`feature-title-${index}`} /></div>
            </div>
            <div><Label>Description</Label><Textarea value={feature.description} onChange={(e) => updateFeature(index, 'description', e.target.value)} rows={2} data-testid={`feature-desc-${index}`} /></div>
            <div><Label>Quote (optional)</Label><Textarea value={feature.quote} onChange={(e) => updateFeature(index, 'quote', e.target.value)} rows={2} placeholder="User testimonial for this feature..." /></div>
            <div>
              <Label>Feature Screenshot</Label>
              <div className="flex items-center gap-2 mt-1 mb-2">
                <Switch
                  checked={feature.showBrowserFrame !== false}
                  onCheckedChange={(checked) => updateFeature(index, 'showBrowserFrame', checked)}
                  className="scale-75"
                  data-testid={`feature-browser-frame-${index}`}
                />
                <span className="text-xs text-gray-500">Show browser frame around image</span>
              </div>
              {feature.image && (
                <div className="mt-2 mb-3 relative rounded-lg overflow-hidden border border-gray-200 bg-white">
                  <img
                    src={resolveImageUrl(feature.image)}
                    alt="Feature preview"
                    className="w-full h-40 object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                    data-testid={`feature-image-preview-${index}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => updateFeature(index, 'image', '')}
                    className="absolute top-2 right-2 h-7 w-7 p-0 bg-white/80 hover:bg-red-50 text-red-500 rounded-full shadow-sm"
                    data-testid={`feature-image-remove-${index}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
              <label
                className={`flex items-center gap-2 px-3 py-2.5 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors ${uploadingIndex === index ? 'opacity-50 pointer-events-none' : ''}`}
                data-testid={`feature-image-upload-${index}`}
              >
                <Upload className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-500">
                  {uploadingIndex === index ? 'Uploading...' : feature.image ? 'Replace image' : 'Upload screenshot'}
                </span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    handleImageUpload(index, e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
